import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, Buildings, Trash, EnvelopeSimple, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";

export default function Admin() {
  const api = useApi();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/workspaces");
      setWorkspaces(r.data || []);
    } catch (e) {
      toast.error("Couldn't load workspaces");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const createWorkspace = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Enter a workspace name and an email to invite");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/workspaces", { name: name.trim(), admin_email: email.trim() });
      toast.success(`Workspace created — an invite email was sent to ${email.trim()}`);
      setName("");
      setEmail("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the workspace");
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    const org = deleteTarget;
    setDeleteTarget(null);
    setDeleting(true);
    try {
      await api.delete(`/admin/workspaces/${org.id}`);
      toast.success(`Deleted "${org.name}"`);
      setWorkspaces((prev) => prev.filter((w) => w.id !== org.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete that workspace");
    } finally {
      setDeleting(false);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

  return (
    <div className="space-y-8 max-w-2xl" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-[12px] bg-[#5FA8B4] flex items-center justify-center text-white shrink-0">
          <ShieldCheck size={22} weight="fill" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#3C4048] tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Admin Panel</h1>
          <p className="text-[#7A9090] text-sm">Create and manage client workspaces — only visible to you.</p>
        </div>
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-4" data-testid="create-workspace-card">
        <div>
          <Label className="text-[#3C4048] mb-2 block font-semibold">New client workspace</Label>
          <p className="text-[#7A9090] text-xs mb-3">
            Creates a brand-new, empty workspace and emails the person below an invite to set their own
            password and log in as its admin.
          </p>
        </div>
        <div>
          <Label htmlFor="workspace-name" className="text-[#3C4048] mb-1.5 block text-sm">Workspace / client name</Label>
          <Input id="workspace-name" data-testid="workspace-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sunrise Wellness Studio" className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048]" />
        </div>
        <div>
          <Label htmlFor="workspace-admin-email" className="text-[#3C4048] mb-1.5 block text-sm">Client's email (they'll be invited as admin)</Label>
          <Input id="workspace-admin-email" data-testid="workspace-admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@theirbusiness.com" className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048]" />
        </div>
        <Button data-testid="create-workspace-btn" onClick={createWorkspace} disabled={creating} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] gap-2">
          <EnvelopeSimple size={16} weight="fill" /> {creating ? "Creating…" : "Create & Send Invite"}
        </Button>
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-4" data-testid="workspaces-list-card">
        <div className="flex items-center gap-2">
          <Buildings size={18} className="text-[#3A7A8C]" />
          <Label className="text-[#3C4048] font-semibold">All workspaces</Label>
        </div>
        {loading ? (
          <p className="text-[#7A9090] text-sm">Loading…</p>
        ) : workspaces.length === 0 ? (
          <p className="text-[#7A9090] text-sm" data-testid="workspaces-empty">No workspaces yet.</p>
        ) : (
          <div className="divide-y divide-[#e5e7eb]">
            {workspaces.map((w) => (
              <div key={w.id} className="py-3 flex items-center justify-between gap-3" data-testid={`workspace-row-${w.id}`}>
                <div className="min-w-0">
                  <p className="text-[#3C4048] font-semibold text-sm truncate">{w.name}</p>
                  <p className="text-[#7A9090] text-xs flex items-center gap-1.5 mt-0.5">
                    <Users size={12} /> {w.members_count} member{w.members_count === 1 ? "" : "s"} · created {fmt(w.created_at)}
                  </p>
                </div>
                <button data-testid={`delete-workspace-btn-${w.id}`} disabled={deleting} onClick={() => setDeleteTarget(w)}
                  className="flex items-center gap-1.5 text-[#7A9090] hover:text-[#ef4444] transition-colors text-xs font-medium shrink-0">
                  <Trash size={13} /> Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>
              Delete "{deleteTarget?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">
              This permanently deletes the workspace and everything in it — posts, media, brand profile,
              and every member's access. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-workspace-cancel" className="border-[#e5e7eb] text-[#3C4048]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-workspace-confirm" onClick={confirmDelete} className="bg-[#ef4444] text-white hover:bg-[#dc2626]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
